"use client";

import { useUser } from "@clerk/nextjs";
import { ResponsiveDialog } from "@repo/ui/components/responsive-dialog";
import { useIsMobile } from "@repo/ui/lib/hooks/use-mobile";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import ClassicLoader from "@/components/common/classic-loader";
import { Button } from "@/components/ui/button";
import { SessionRecordingGuard } from "@/features/analytics/components/session-recording-guard";
import { AuthSteps } from "@/features/auth/components/auth-steps";
import { AccountBenefitsList } from "@/features/auth/components/elements/account-benefits-list";
import { AuthStepHeader } from "@/features/auth/components/elements/auth-step-header";
import { useAuthFlow } from "@/features/auth/hooks/use-auth-flow";
import {
  SSO_CALLBACK_PARAM,
  SSO_CALLBACK_VALUE,
} from "@/features/auth/hooks/use-sso-return";
import { AUTH_PRIMARY_BUTTON } from "@/features/auth/lib/auth-button-styles";
import {
  AUTH_FOOTER_STEPS,
  AUTH_TITLE_KEYS,
  isAuthFooterStep,
} from "@/features/auth/lib/auth-step-config";
import { getSignInPathname } from "@/features/auth/lib/sign-in-redirect";
import { useAuthDialogStore } from "@/features/auth/store/auth-dialog-store";
import { getLocaleForPathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

/**
 * The shared "Logowanie lub rejestracja" entry point: a dialog on desktop and a
 * bottom drawer on mobile. Mounted once in the layout and opened from anywhere
 * through `useAuthDialogStore`.
 */
export const AuthDialog = () => {
  const t = useTranslations("auth");
  const isMobile = useIsMobile();
  const locale = getLocaleForPathname();
  const { isSignedIn } = useUser();

  const isOpen = useAuthDialogStore((store) => store.isOpen);
  const redirectUrl = useAuthDialogStore((store) => store.redirectUrl);
  const closeAuthDialog = useAuthDialogStore((store) => store.closeAuthDialog);

  const signInPath = getSignInPathname({ locale });

  const flow = useAuthFlow({
    onSignedIn: closeAuthDialog,
    redirectUrl,
    ssoCallbackUrl: `${signInPath}?${SSO_CALLBACK_PARAM}=${SSO_CALLBACK_VALUE}`,
  });

  const resetToEmailStep = flow.resetToEmailStep;

  // A session can also start outside `finalize()`'s navigation callback — an
  // OAuth round trip, or a redirect that lands back on the same page — so the
  // dialog closes on the session itself instead of trusting a single callback.
  useEffect(() => {
    if (!(isOpen && isSignedIn)) {
      return;
    }

    closeAuthDialog();
    resetToEmailStep();
  }, [closeAuthDialog, isOpen, isSignedIn, resetToEmailStep]);

  const isEmailStep = flow.step === "email";
  const footerForm =
    isMobile && isAuthFooterStep(flow.step)
      ? AUTH_FOOTER_STEPS[flow.step]
      : null;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }

    closeAuthDialog();
    flow.resetToEmailStep();
  };

  const header = (
    <AuthStepHeader
      onBack={isEmailStep ? undefined : flow.resetToEmailStep}
      title={t(AUTH_TITLE_KEYS[flow.step])}
    />
  );

  return (
    <ResponsiveDialog
      closeLabel={t("close")}
      contentClassName={cn(
        "flex flex-col gap-6 px-4 pb-6 md:max-h-[75vh] md:overflow-y-auto md:px-6 md:pt-6",
        // Steps that open with the grey info box sit flush under the mobile
        // header; the e-mail form gets breathing room instead.
        isEmailStep ? "pt-6" : "pt-0",
        // Steps with a pinned CTA fill the sheet so the step can push its
        // consents down to the bottom bar, the way the design has them.
        footerForm && "max-md:min-h-full",
      )}
      dialogClassName="gap-0 overflow-hidden p-0 md:max-w-[480px]"
      // The sheet keeps the height from the design (40px below the top edge)
      // instead of following its content, so steps of different length do not
      // resize it.
      drawerClassName="h-[calc(100dvh-40px)] max-h-[calc(100dvh-40px)]"
      footer={
        footerForm ? (
          <Button
            className={AUTH_PRIMARY_BUTTON}
            disabled={flow.isSubmitting}
            form={footerForm.formId}
            type="submit"
          >
            {t(footerForm.labelKey)}
            {flow.isSubmitting && <ClassicLoader className="ml-2" />}
          </Button>
        ) : undefined
      }
      footerClassName="w-full border-t px-4 py-4 md:px-6"
      header={header}
      headerClassName="w-full px-0 py-0"
      onOpenChange={handleOpenChange}
      open={isOpen}
    >
      {isOpen && <SessionRecordingGuard />}
      <AuthSteps
        className={cn(footerForm && "max-md:flex-1")}
        flow={flow}
        showEmailHeading={false}
        submitInFooter={Boolean(footerForm)}
      />

      {isEmailStep && (
        <AccountBenefitsList className="-mx-4 bg-background-surface-secondary px-4 py-6 md:-mx-6 md:px-6" />
      )}
    </ResponsiveDialog>
  );
};
